<!-- Status Bar: responsive user/entity/session strip -->
<div
  class="flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors duration-200"
  [class.bg-white]="!isDarkMode()"
  [class.border-gray-200]="!isDarkMode()"
  [class.bg-neutral-800]="isDarkMode()"
  [class.border-neutral-700]="isDarkMode()">

  <!-- Avatar — always visible; also the notifications entry point. Tooltip shows
       full user details; click opens the alerts panel (refreshes claims, no polling). -->
  <button
    #avatarButton
    type="button"
    data-testid="status-avatar-btn"
    class="relative shrink-0 inline-flex p-0 border-0 bg-transparent leading-none cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
    [class.focus-visible:ring-offset-white]="!isDarkMode()"
    [class.focus-visible:ring-offset-neutral-800]="isDarkMode()"
    tooltipPosition="bottom"
    [attr.aria-label]="avatarAriaLabel()"
    aria-haspopup="dialog"
    [attr.aria-expanded]="panelOpen()"
    (click)="onAvatarClick($event, alertasPanel)">
    <p-overlaybadge
      [value]="alertasPendientesLabel()"
      severity="danger"
      [badgeDisabled]="alertasPendientes() === 0">
      <p-avatar
        [label]="avatarLabel()"
        shape="circle"
        size="normal"
        [style]="{ 'background-color': isDarkMode() ? '#34d399' : '#059669', 'color': '#fff', 'font-weight': '600', 'flex-shrink': '0' }" />
    </p-overlaybadge>
  </button>

  <!-- Notifications panel — anchored to the avatar button; escapes any
       overflow-hidden ancestor via appendTo="body". F16: `styleClass` carries no CSS
       of its own today (styles.css has no `.status-alertas-popover` rule) -- it's
       kept as the stable hook status-bar.spec.ts's `panelEl()` uses to find the
       overlay's content under `document.body` (appendTo moves it out of
       `fixture.nativeElement`), and as the attachment point for any panel-specific
       CSS this needs later. -->
  <p-popover
    #alertasPanel
    appendTo="body"
    styleClass="status-alertas-popover"
    ariaLabel="Notificaciones"
    (onHide)="onPanelHide()">
    <!-- F15: `autofocus` here isn't a page-load anti-pattern -- PrimeNG's own
         Popover.focus() targets the `[autofocus]` element inside its content to move
         focus into the just-opened overlay (WAI-ARIA dialog pattern), same as
         `tabindex="-1"` making this div itself focusable for that. -->
    <!-- eslint-disable-next-line @angular-eslint/template/no-autofocus -->
    <div class="flex flex-col w-[min(90vw,340px)] focus:outline-none" tabindex="-1" autofocus>
      <!-- Header -->
      <div
        class="flex items-center justify-between gap-2 px-1 pb-2 mb-1 border-b"
        [class.border-gray-200]="!isDarkMode()"
        [class.border-neutral-700]="isDarkMode()">
        <span
          class="text-sm font-semibold"
          [class.text-gray-800]="!isDarkMode()"
          [class.text-neutral-100]="isDarkMode()">
          Notificaciones
        </span>
        @if (panelLoading()) {
          <i
            class="pi pi-spin pi-spinner text-xs"
            [class.text-gray-400]="!isDarkMode()"
            [class.text-neutral-500]="isDarkMode()"
            aria-hidden="true"></i>
        } @else if (alertasPendientes() > 0) {
          <span
            class="text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0"
            [class.bg-emerald-50]="!isDarkMode()"
            [class.text-emerald-700]="!isDarkMode()"
            [class.bg-emerald-900]="isDarkMode()"
            [class.text-emerald-300]="isDarkMode()">
            {{ alertasPendientesLabel() }} sin leer
          </span>
        }
      </div>

      <!-- List / empty state -->
      @if (alertasView().length === 0) {
        <div class="flex flex-col items-center text-center gap-1.5 px-4 py-8" data-testid="alertas-empty">
          <i
            class="pi pi-bell text-2xl"
            [class.text-gray-300]="!isDarkMode()"
            [class.text-neutral-600]="isDarkMode()"
            aria-hidden="true"></i>
          <span
            class="text-sm font-medium"
            [class.text-gray-600]="!isDarkMode()"
            [class.text-neutral-300]="isDarkMode()">
            Sin notificaciones pendientes
          </span>
          <span
            class="text-xs"
            [class.text-gray-400]="!isDarkMode()"
            [class.text-neutral-500]="isDarkMode()">
            Aquí verás las peticiones y sugerencias que te envíen.
          </span>
        </div>
      } @else {
        <div class="flex flex-col gap-0.5 max-h-[60vh] overflow-y-auto -mx-1 px-1" role="list">
          @for (alerta of alertasView(); track alerta.id) {
            <!-- F7: the listitem role lives on this wrapping div, NOT the button --
                 a listitem should hold the list's content, while the button keeps
                 its own implicit (and unambiguous, for AT) "button" role.
                 P04/T8: the row itself is a plain div now (not a button) because it
                 hosts TWO interactive controls side by side (open + resolve) -- nesting
                 a button inside a button is an a11y/AXE violation. -->
            <div role="listitem">
            <div
              class="flex items-start gap-1 w-full rounded-md transition-colors duration-150"
              [class.hover:bg-gray-50]="!isDarkMode()"
              [class.hover:bg-neutral-700]="isDarkMode()">
            <button
              type="button"
              data-testid="alerta-item"
              class="flex items-start gap-2.5 flex-1 min-w-0 text-left px-2 py-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              (click)="onAlertaClick(alerta, alertasPanel)">
              <span
                class="flex items-center justify-center w-8 h-8 rounded-full shrink-0"
                [class.bg-emerald-50]="!isDarkMode()"
                [class.text-emerald-600]="!isDarkMode()"
                [class.bg-emerald-900]="isDarkMode()"
                [class.text-emerald-300]="isDarkMode()">
                <i [class]="categoriaIcono(alerta.categoria) + ' text-sm'" aria-hidden="true"></i>
              </span>

              <span class="flex flex-col min-w-0 flex-1 gap-0.5 pt-0.5">
                <span class="flex items-center gap-1.5">
                  @if (alerta.estado === 'PENDIENTE') {
                    <span
                      class="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"
                      data-testid="alerta-dot"
                      aria-hidden="true"></span>
                  }
                  <span
                    class="text-xs font-semibold truncate"
                    [class.text-gray-800]="!isDarkMode()"
                    [class.text-neutral-100]="isDarkMode()">
                    @if (alerta.categoria === 'VALIDACION') {
                      {{ alerta.descripcion }}
                    } @else {
                      {{ categoriaLabel(alerta.categoria) }} · {{ alerta.form_descripcion || alerta.form_nombre || 'Formulario' }}
                    }
                  </span>
                </span>
                <span
                  data-testid="alerta-meta"
                  class="text-[11px] truncate"
                  [class.text-gray-500]="!isDarkMode()"
                  [class.text-neutral-400]="isDarkMode()">
                  <!-- F12: no dangling "·" when tiempoRelativo() can't parse creado_en
                       (empty string -- see its own doc comment for when that happens). -->
                  De {{ origenLabel(alerta) }}
                  @if (tiempoRelativo(alerta.creado_en); as relativo) {
                    · {{ relativo }}
                  }
                </span>
                @if (alerta.categoria !== 'VALIDACION' && alerta.descripcion) {
                  <span
                    class="text-[11px] line-clamp-2"
                    [class.text-gray-500]="!isDarkMode()"
                    [class.text-neutral-400]="isDarkMode()">
                    {{ alerta.descripcion }}
                  </span>
                }
                @if (validacionNombreDe(alerta); as validacionNombre) {
                  <span
                    class="text-xs truncate"
                    [class.text-gray-500]="!isDarkMode()"
                    [class.text-neutral-400]="isDarkMode()">
                    {{ validacionNombre }}
                  </span>
                }
              </span>
            </button>
            @if (alerta.categoria === 'VALIDACION') {
              <button
                type="button"
                data-testid="alerta-item-resolver"
                aria-label="Marcar resuelta"
                class="shrink-0 mt-2 mr-1 inline-flex items-center justify-center w-7 h-7 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                [class.text-gray-500]="!isDarkMode()"
                [class.hover:bg-gray-100]="!isDarkMode()"
                [class.hover:text-emerald-600]="!isDarkMode()"
                [class.text-neutral-400]="isDarkMode()"
                [class.hover:bg-neutral-600]="isDarkMode()"
                [class.hover:text-emerald-300]="isDarkMode()"
                (click)="onResolverClick(alerta, $event)">
                <i class="pi pi-check text-xs" aria-hidden="true"></i>
              </button>
            }
            </div>
            </div>
          }
        </div>
      }
    </div>
  </p-popover>

  <!-- ── User column (always visible) ── -->
  <div class="flex flex-col min-w-0 flex-1">

    <!-- Row 1: nombre_completo -->
    <span
      class="text-xs font-semibold truncate leading-tight"
      [class.text-gray-800]="!isDarkMode()"
      [class.text-neutral-100]="isDarkMode()">
      {{ claims()?.nombre_completo ?? '—' }}
    </span>

    <!-- Row 2: email + doc (hidden on xs, visible sm+) -->
    <span
      class="hidden sm:block text-[10px] truncate leading-tight mt-px"
      [class.text-gray-500]="!isDarkMode()"
      [class.text-neutral-400]="isDarkMode()">
      {{ claims()?.correo_electronico ?? '' }}
      @if (claims()?.numero_documento) {
        &nbsp;&middot;&nbsp;Doc:&nbsp;{{ claims()!.numero_documento }}
      }
    </span>

    <!-- Row 3: deployed app version (build-baked; hidden on xs) -->
    @if (appVersionLabel(); as version) {
      <span
        class="hidden sm:block text-[10px] truncate leading-tight mt-px"
        [class.text-gray-400]="!isDarkMode()"
        [class.text-neutral-500]="isDarkMode()">
        <i class="pi pi-tag mr-0.5" style="font-size: 0.6rem"></i>Versión&nbsp;{{ version }}
      </span>
    }
  </div>

  <!-- ── Divider (hidden on xs) ── -->
  <div
    class="hidden md:block self-stretch w-px mx-1"
    [class.bg-gray-200]="!isDarkMode()"
    [class.bg-neutral-600]="isDarkMode()">
  </div>

  <!-- ── Entity column (hidden on xs/sm, visible md+) ── -->
  <div class="hidden md:flex flex-col min-w-0 flex-1">

    <!-- entidad.nombre -->
    <span
      class="text-[10px] font-semibold truncate leading-tight"
      [class.text-gray-700]="!isDarkMode()"
      [class.text-neutral-200]="isDarkMode()"
      [pTooltip]="claims()?.entidad?.nombre ?? ''"
      tooltipPosition="bottom">
      {{ claims()?.entidad?.nombre ?? '—' }}
    </span>

    <!-- entidad.tipo · entidad.codigo -->
    <span
      class="text-[10px] truncate leading-tight mt-px"
      [class.text-gray-500]="!isDarkMode()"
      [class.text-neutral-400]="isDarkMode()">
      @if (claims()?.entidad?.tipo) {
        {{ claims()!.entidad.tipo }}
      }
      @if (claims()?.entidad?.codigo) {
        &nbsp;&middot;&nbsp;{{ claims()!.entidad.codigo }}
      }
    </span>

    <!-- departamento · municipio -->
    @if (locationLine()) {
      <span
        class="text-[10px] truncate leading-tight mt-px"
        [class.text-gray-400]="!isDarkMode()"
        [class.text-neutral-500]="isDarkMode()">
        <i class="pi pi-map-marker mr-0.5" style="font-size: 0.6rem"></i>{{ locationLine() }}
      </span>
    }
  </div>

  <!-- ── Session expiry (hidden on xs, visible sm+) ── -->
  @if (sessionExpiry()) {
    <div
      class="hidden sm:flex flex-col items-end shrink-0"
      [pTooltip]="'Sesión expira: ' + (sessionExpiry() | date: 'HH:mm a')"
      tooltipPosition="bottom">
      <span
        class="text-[9px] uppercase font-semibold tracking-wide"
        [class.text-gray-400]="!isDarkMode()"
        [class.text-neutral-500]="isDarkMode()">
        Sesión
      </span>
      <span
        class="text-xs font-mono font-medium"
        [class.text-emerald-700]="!isDarkMode()"
        [class.text-emerald-400]="isDarkMode()">
        {{ sessionExpiry() | date: 'HH:mm' }}
      </span>
    </div>
  }

  <!-- ── Manual de Usuario (visible to any user) ── -->
  <a
    href="/docs/usuario/"
    target="_blank"
    rel="noopener noreferrer"
    class="flex items-center gap-1.5 shrink-0 px-2 py-1 rounded-md transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
    [class.text-gray-600]="!isDarkMode()"
    [class.hover:bg-gray-100]="!isDarkMode()"
    [class.text-neutral-300]="isDarkMode()"
    [class.hover:bg-neutral-700]="isDarkMode()"
    [pTooltip]="'Manual de Usuario'"
    tooltipPosition="bottom">
    <i class="pi pi-book" style="font-size: 0.9rem"></i>
    <span class="hidden lg:inline text-[11px] font-medium">Manual de Usuario</span>
  </a>
</div>
